<script setup lang="ts">
/**
 * Компонент bottom sheet для настроек продукта на мобильных
 * Использует useBottomSheet композабл
 * Следует Vue Style Guide: Priority A
 */

import { computed } from 'vue';
import type { ProductVariant, ProductAddon, ProductExclude } from '../model/types/product.types';
import ProductAddons from './ProductAddons.vue';
import ProductExcludes from './ProductExcludes.vue';
import { useBottomSheet } from '~/shared/lib';

interface Props {
  modelValue: boolean;
  currentVariant: ProductVariant;
  selectedAddons: number[];
  selectedExcludes: number[];
  visibleAddons: ProductAddon[];
  visibleExcludes: ProductExclude[];
  hasMoreAddons: boolean;
  hasMoreExcludes: boolean;
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void;
  (e: 'update:selectedAddons', value: number[]): void;
  (e: 'update:selectedExcludes', value: number[]): void;
  (e: 'showMoreAddons'): void;
  (e: 'showMoreExcludes'): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

// Bottom sheet логика
const { sheetRef, scrollInnerRef, handleTouchStart, handleTouchMove, handleTouchEnd } = useBottomSheet({
  onClose: () => emit('update:modelValue', false),
});

// v-model для открытия/закрытия
const isOpen = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
});

// v-model для addons
const addons = computed({
  get: () => props.selectedAddons,
  set: (value: number[]) => emit('update:selectedAddons', value),
});

// v-model для excludes
const excludes = computed({
  get: () => props.selectedExcludes,
  set: (value: number[]) => emit('update:selectedExcludes', value),
});
</script>

<template>
  <!-- Телепортируем в body: иначе position:fixed перехватывается трансформированным
       контейнером модалки (UModal panel) и обрезается её overflow:hidden — модификаторы
       «режутся», как будто у родителя стоит overflow:hidden. -->
  <Teleport to="body">
    <!-- Оверлей -->
    <Transition name="fade">
      <div v-if="isOpen" class="product-settings-sheet__overlay" @click="isOpen = false" />
    </Transition>

    <!-- Bottom Sheet -->
    <Transition name="slide-up">
      <div
        v-if="isOpen"
        ref="sheetRef"
        class="product-settings-sheet"
        @touchstart="handleTouchStart"
        @touchmove="handleTouchMove"
        @touchend="handleTouchEnd"
      >
      <!-- Заголовок с индикатором -->
      <div class="product-settings-sheet__header">
        <!-- Индикатор свайпа -->
        <div class="product-settings-sheet__handle">
          <div class="product-settings-sheet__handle-bar" />
        </div>

        <!-- Название продукта -->
        <div class="product-settings-sheet__title">
          {{ currentVariant.name }}
        </div>
      </div>

      <!-- Прокручиваемый контент -->
      <div ref="scrollInnerRef" class="product-settings-sheet__content">
        <!-- Аддоны -->
        <ProductAddons
          v-if="visibleAddons.length"
          v-model="addons"
          class="product-settings-sheet__addons"
          :addons="visibleAddons"
          is-mobile
        >
          <template #show-more-button>
            <UButton
              v-if="hasMoreAddons"
              variant="ghost"
              color="white"
              block
              size="sm"
              icon="heroicons:chevron-down-20-solid"
              trailing
              style="background: transparent"
              class="mt-2 w-full text-white"
              @click="emit('showMoreAddons')"
            >
              Показать ещё
            </UButton>
          </template>
        </ProductAddons>

        <!-- Исключения -->
        <ProductExcludes
          v-if="visibleExcludes.length"
          v-model="excludes"
          class="product-settings-sheet__excludes"
          :excludes="visibleExcludes"
          is-mobile
        >
          <template #show-more-button>
            <UButton
              v-if="hasMoreExcludes"
              variant="ghost"
              color="white"
              size="sm"
              block
              icon="heroicons:chevron-down-20-solid"
              trailing
              style="background: transparent"
              class="mt-2 w-full text-white"
              @click="emit('showMoreExcludes')"
            >
              Показать ещё
            </UButton>
          </template>
        </ProductExcludes>
      </div>
    </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="scss">
.product-settings-sheet {
  @media (max-width: 680px) {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: calc(0px + var(--safe-area-inset-top));
    left: 0;
    right: 0;
    bottom: 85px; // Место для кнопок внизу
    z-index: 52;
    padding: 20px;
    margin: 0;
    border-radius: 16px 16px 0 0;
    min-height: 0;
    // Непрозрачный фон — иначе сквозь sheet просвечивает модалка (дубли заголовка, «Собрать набор»).
  }

  @media (min-width: 681px) {
    display: none;
  }

  &__overlay {
    @media (max-width: 680px) {
      display: block;
      position: fixed;
      top: calc(0px + var(--safe-area-inset-top));
      left: 0;
      right: 0;
      // Оставляем нижнюю полосу свободной под кнопки ProductActions (как и сам sheet:
      // bottom: 85px). Sheet телепортирован в body, поэтому кнопки модалки иначе
      // оказались бы под оверлеем — здесь они остаются видимыми и кликабельными.
      bottom: 85px;
      z-index: 51;
      backdrop-filter: blur(18px);
      -webkit-backdrop-filter: blur(18px);
      background-color: rgba(0, 0, 0, 0.3);
    }

    @media (min-width: 681px) {
      display: none;
    }
  }

  &__header {
    @apply flex flex-col flex-shrink-0 mb-4 pb-4 border-b border-gray-300 dark:border-gray-700;
  }

  &__handle {
    @apply flex justify-center mb-3;
  }

  &__handle-bar {
    @apply w-12 h-1 bg-gray-400 dark:bg-gray-600 rounded-full;
  }

  &__title {
    @apply text-2xl font-semibold text-white break-words;
  }

  // Единый скролл-контейнер. Был display:grid + flex-свойства у детей (flex:1 /
  // margin-top:auto) — в grid-контексте они не работают, треки считались неверно и
  // блок добавок наезжал на «Убрать ингредиенты». Возвращено к flex-колонке (как
  // задумано детьми): min-height:0 включает скролл, дети — натуральной высоты.
  &__content {
    display: flex;
    flex-direction: column;
    flex: 1 1 0%;
    min-height: 0;
    gap: 2rem;
    overflow-x: hidden;
    overflow-y: auto;
    padding-right: 0.5rem;
  }

  &__addons {
    display: flex;
    flex-direction: column;
  }

  &__excludes {
    flex-shrink: 0;
  }
}

// Анимации
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease-out;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
}
</style>
